import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit
} from '@angular/core';
import {isEqual, intersection, difference, orderBy} from 'lodash';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {RollInRollOutModalComponent} from '../roll-in-roll-out-modal/roll-in-roll-out-modal';
import {AutoDestroy, SessionStorageService} from '@ox/utils';
import {
  Copy,
  EmitObject,
  Plans,
  Prices,
  ProductCopy,
  UpdatePlan
} from '@ox/typings';
import {OX_KEYS} from '@ox/config';
import {Subject, take} from 'rxjs';
import {AppState, selectActiveVehicle, selectMyCurrentPlans, TrackingService} from '@ox/data';
import {Store} from '@ngrx/store';
import {ShopDataService} from '@webu/data';
import {ServiceModalComponent} from '../service-modal/service-modal';
import {replaceVariable} from '@ox/utils';

@Component({
  selector: 'webu-build-your-own',
  templateUrl: './build-your-own.component.html'
})
export class BuildYourOwnComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() copy: Copy;
  @Input() updatePlanData: UpdatePlan;
  @Input() products: ProductCopy[];
  @Input() addtext: string;
  @Input() addAndSavetext: string;
  @Input() removetext: string;

  private _selectedProduct: Plans;
  @Input()
  set selectedProduct(value: Plans) {
    this._selectedProduct = value;
    this.updateSelectedProducts(false);
  }

  get selectedProduct(): Plans {
    return this._selectedProduct;
  }

  @Output() selectedProductChange = new EventEmitter();
  @Output() selectedProductAndResidue = new EventEmitter();

  bsModalRef?: BsModalRef;
  lastEmitObject: EmitObject;
  byoProducts: Plans[] = [];
  addAndSaveProducts: {[key: string]: string};
  private myCurrentPlans: string[] = [];
  sampleplanindex: number;
  checkoutTrackingString: {products?: string; eVar231?: string} = {};

  activeVehicle$ = this.store.select(selectActiveVehicle);
  productString: string;
  constructor(
    private modalService: BsModalService,
    private sessionStorageSvc: SessionStorageService,
    private store: Store<AppState>,
    public trackingService: TrackingService,
    private shopDataService: ShopDataService
  ) {}

  ngOnInit(): void {
    this.store.select(selectMyCurrentPlans)
      .pipe(take(1))
      .subscribe((myCurrentPlans)=> this.myCurrentPlans = myCurrentPlans??[]);

    this.byoProducts = this.getOrderedProducts().map(
      (item: Plans) => this.getProductWithOffer(item)
      );

    this.updateSelectedProducts(true);
  }

  private getOrderedProducts(): any[] {
    return orderBy(this.updatePlanData?.byoPlans, [
      (item) => this.getCurrentPlanSelected(item),
      'priority'
    ]);
  }

  private getCurrentPlanSelected(item: any): number {
    const planCodeAndId = item.offers.flatMap((offer:any) => [
      offer.planCode,
      offer.planId
    ]);
    const currentPlanSelected = planCodeAndId.some((codeOrId: any) => this.myCurrentPlans.includes(codeOrId || ''));
    return currentPlanSelected ? 0 : 1;
  }

  private getProductWithOffer(item: Plans): any {
    const product: any = this.findProductInOffers(item);

    const offer = this.updatePlanData.offers.find(
      (offerItem: any) => offerItem.planCode === product.code
    );

    this.updateProductWithOffer(product, offer, item);

    return product;
  }

  private findProductInOffers(item: Plans): any {
    let planCode;
    if (item.offers.length > 1) {
      const offerIndex = item.offers.findIndex((offerItem) =>
        this.selectComposite(offerItem.planCode)
      );
      planCode = item.offers[offerIndex > 0 ? offerIndex : 0].planCode;
    } else {
      planCode = item.offers[0].planCode;
    }

    return this.findProduct(planCode);
  }

  private updateProductWithOffer(product: any, offer: any, item: Plans): void {
    product.annualDiscountTaglineCopy = offer?.annualDiscountTaglineCopy;
    product.offer = offer;
    if (offer) {
      product.price = offer.prices.find(
        (priceItem: any) => priceItem.billingFrequency === OX_KEYS.MY_PLANS.MONTHLY_KEY
      );
    }

    product.offers = item.offers;
    if (item.offers && item.offers.length > 1) {
      product.multiCardDisplayName = item.title;
    }
  }

  addOrRemoveTagging(id: number, productName: string) {
    const product = this.byoProducts[id];
    const actionText = this.addOrRemoveText(product);
    const event = product.selected ? 'event231' : 'event232';

    const trackingString = `shop:update plan:${productName}:button:${actionText}`;

    this.trackingService.fireProp10Tracking(
      trackingString,
      '',
      '',
      actionText,
      '',
      true,
      event
    );
  }

  generateAnalyticsInfo(id: number, product: Plans) {
    product.selected
      ? this.trackAnalyticsEvent(product, 'remove', 'event231', id)
      : this.trackAnalyticsEvent(product, 'add and save', 'event232', id);
  }

  private trackAnalyticsEvent(product: any, label: string, event: string, id: number) {
    const actionText = this.addOrRemoveText(this.byoProducts[id]);
    const productViewString = this.trackingService?.createProdViewString(product.offer, actionText, false);
  
    const trackingData = {
      prop10: `shop:update plan:${product.displayName}:button:${label}`,
      prop15: label,
      evar33: `shop:update plan:${product.displayName}:button:${label}`,
      products: productViewString
    };
  
    this.trackingService?.trackCustomEvent(event, trackingData, label);
  
    this.checkoutTrackingString['products'] = productViewString;
    this.checkoutTrackingString['eVar231'] = 'event231';
  }

  trackProductClickEvent(product: any, item: any, action: string, id?: string): void {
    const name = item.name || item.planId;
    const trackingString = `shop:update plan:${product.displayName || product.multiCardDisplayName}:${action}:${name}:${id}`;
    this.trackingService.fireProp10Tracking(trackingString, '', '', id, '', true);
  }

  addOrRemove(id: number) {
    const text = this.addOrRemoveText(this.byoProducts[id]);
    const selected = this.byoProducts[id].selected;

    this.byoProducts[id].selected = !selected;
    this.findAndEmitProduct(text);
  }

  addOrRemoveText(product: Plans) {
    let text = this.addtext;
    if (product.selected) {
      text = this.removetext;
    } else if (this.addAndSaveProducts[product.code]) {
      text = this.addAndSavetext;
    }
    return text;
  }

  selectComposite(name: string | undefined) {
    return !!this.selectedProduct?.compositeProducts?.find((item: string) =>
      item === OX_KEYS.MY_PLANS.UNLIMITED_DATA_KEY
        ? name === OX_KEYS.MY_PLANS.RECURRING_UNLIMITED_30_KEY
        : item === name
    );
  }

  getSelectedBYO(): string[] {
    const footerInfo: EmitObject =
      this.sessionStorageSvc.getFromJson('FooterInfo');
    return (
      footerInfo?.selectedBYO?.map((item) => item.code) || this.myCurrentPlans
    );
  }

  isCurrentPlanSelected(
    init: boolean,
    item: Plans,
    selectedBYO: string[]
  ): boolean {
    if (!init) return false;

    const planCodeAndId = item.offers.flatMap(offer => [
      offer.planCode,
      offer.planId
    ]);
    return planCodeAndId.some(codeOrId => selectedBYO.includes(codeOrId || ''));
  }

  getSelectedOfferIndex(
    init: boolean,
    item: Plans,
    selectedBYO: string[]
  ): number {
    const multiOffer = item.offers.length > 1;
    return multiOffer &&
      ((init && selectedBYO.indexOf(item.offers[1].planCode || '') >= 0) ||
        this.selectComposite(item.offers[1].planCode))
      ? 1
      : this.selectComposite(item.offers[0].planCode)
      ? 0
      : this.lastEmitObject?.residueBYO.length > 0
      ? item.selectedOfferIndex
      : 0;
  }

  updateSelectedProducts(init: boolean) {
    if (this.updatePlanData) {
      const selectedBYO = this.getSelectedBYO();

      this.byoProducts.forEach((item: Plans, index: number) => {
        const currentPlanSelected = this.isCurrentPlanSelected(
          init,
          item,
          selectedBYO
        );
        const selectedOfferIndex = this.getSelectedOfferIndex(
          init,
          item,
          selectedBYO
        );

        const selected = this.selectedProduct
          ? item.offers.length > 1 &&
            (this.selectComposite(item.offers[0].planCode) ||
              this.selectComposite(item.offers[1].planCode))
            ? true
            : this.selectComposite(item.code)
          : item.selected || currentPlanSelected;

        item.selected = selected;
        item.selectedOfferIndex = selectedOfferIndex;

        if (item.code !== item.offers[selectedOfferIndex].planCode) {
          this.setValue(index, item, selectedOfferIndex, false);
        }
      });

      this.findAndEmitProduct();
    }
  }

  getSelectedByoProducts(): any[] {
    return this.byoProducts.filter((item) => item.selected);
  }

  mapProductCodes(selectedByoProducts: any[]): string[] {
    return selectedByoProducts.map((item) =>
      item.code === OX_KEYS.MY_PLANS.RECURRING_UNLIMITED_30_KEY
        ? OX_KEYS.MY_PLANS.UNLIMITED_DATA_KEY
        : item.code
    );
  }

  findProductInProducts(selected: string[]): any {
    return this.products.find((item) =>
      isEqual(item?.compositeProducts?.sort(), selected.sort())
    );
  }

  handleRemoveText(
    product: any,
    selectedByoProducts: any[],
    text?: string
  ): void {
    if (text === this.removetext) {
      this.openRollInOutModal(
        this.selectedProduct,
        selectedByoProducts,
        text,
        product
      );
    }
  }

  findAndEmitProduct(text?: string) {
    const selectedByoProducts = this.getSelectedByoProducts();
    const selected = this.mapProductCodes(selectedByoProducts);

    this.updateAddandSaveProducts(selected, selectedByoProducts, text);

    if (this.products) {
      const product = this.findProductInProducts(selected);
      if(!product) return;
      this.handleRemoveText(product, selectedByoProducts, text);
      this.selectedProductChange.emit(product);
    }
  }

  findPrice(prices: Prices[], key: string): number {
    const price = prices.find((item: Prices) => item.billingFrequency === key);
    return price ? Number(price.customerFinalPrice) : 0;
  }

  updateMatchProduct(
    currentOffer: any,
    compositeProducts: string[],
    selectedProducts: string[],
    commonProductCount: number
  ) {
    let matchProduct: any;
    let price = 0;
    let matchSelection: any;

    if (
      compositeProducts &&
      compositeProducts.length &&
      selectedProducts.length &&
      commonProductCount === compositeProducts.length
    ) {
      price = this.findPrice(currentOffer.prices, OX_KEYS.MY_PLANS.MONTHLY_KEY);
      matchProduct = currentOffer;
      matchSelection = difference(selectedProducts, compositeProducts);
    }

    return {matchProduct, price, matchSelection};
  }

  updateAddandSaveProducts(selected: string[], selectedBYO: Plans[], text?: string) {
    let matchProduct: any;
    let matchSelection: any;
    let price = 0;

    this.addAndSaveProducts = this.updatePlanData?.offers?.reduce(
      (accumulator: any, currentOffer: any) => {
        const compositeProducts = 
          currentOffer.compositeProducts?.filter((item: string) => item.toLocaleLowerCase() != OX_KEYS.MY_PLANS.SUPER_CRUISE_KEY.toLowerCase());
        const selectedProducts = selected;
        const commonProductCount = intersection(
          compositeProducts,
          selectedProducts
        ).length;
        const extraCompositeProductCount = difference(
          compositeProducts,
          selectedProducts
        ).length;
        const extraSelectedProductCount = difference(
          selectedProducts,
          compositeProducts
        ).length;

        if (!matchProduct) {
          const result = this.updateMatchProduct(
            currentOffer,
            compositeProducts,
            selectedProducts,
            commonProductCount
          );
          matchProduct = result.matchProduct;
          price = result.price;
          matchSelection = result.matchSelection;
        }

        if (
          compositeProducts &&
          compositeProducts.length &&
          selectedProducts.length &&
          commonProductCount > 0 &&
          selectedProducts.length >= commonProductCount &&
          (extraCompositeProductCount === 1 ||
            (extraCompositeProductCount < 1 && extraSelectedProductCount === 1))
        ) {
          const extraCompositeProducts = difference(
            compositeProducts,
            selectedProducts
          );
          extraCompositeProducts.forEach(
            (product) =>
              (accumulator[
                product === OX_KEYS.MY_PLANS.UNLIMITED_DATA_KEY
                  ? OX_KEYS.MY_PLANS.RECURRING_UNLIMITED_30_KEY
                  : product
              ] = '1')
          );
        }
        return accumulator;
      },
      {}
    );

    const residue = (matchProduct ? matchSelection : selected).map(
      (item: string) =>
        this.byoProducts.find((byoProduct: Plans) =>
          item === OX_KEYS.MY_PLANS.UNLIMITED_DATA_KEY
            ? byoProduct.code === OX_KEYS.MY_PLANS.RECURRING_UNLIMITED_30_KEY
            : item === byoProduct.code
        )
    );

    const product =
      matchProduct &&
      this.products.find((item) => item.code === matchProduct.planCode);
    const totalResiduePrice = residue.reduce(
      (total: number, plan: Plans) =>
        total + (plan.price ? Number(plan.price.customerFinalPrice) : 0), 0
    );
    const totalPrice = Number(price) + totalResiduePrice;

    const emitObject = {
      product,
      residueBYO: residue,
      selectedBYO,
      selectedProductPrice: price,
      price: totalPrice.toString(),
      offerIds: [],
      planCount: 0,
      selectedProductName: '',
      priceDisclosure: '',
      selectedProductDisclosure: '',
      isMonthly: true
    };
    this.lastEmitObject = emitObject;
    this.selectedProductAndResidue.emit(emitObject);

    if (!this.products) {
      return;
    }

    this.openRollInOutModal(product, selectedBYO, text);
  }

  openRollInOutModal(
    matchProduct: Plans,
    items: Plans[],
    text?: string,
    product?: any
  ) {
    const isRemove = text === this.removetext;
    const sessionName = isRemove ? 'removeProduct' : 'addAndSaveProduct';

    const productsAddOpened = this.getSessionProducts('addAndSaveProduct');
    const productsOpened = this.getSessionProducts(sessionName);

    const matchProductItem = this.products.find(
      (item) =>
        item &&
        item.code ===
          (matchProduct ? matchProduct.code : this.selectedProduct?.code)
    );

    if (isRemove) {
      this.handleRemoveCase(
        productsOpened,
        productsAddOpened,
        items,
        product,
        text
      );
    } else if (
      text === this.addAndSavetext &&
      matchProduct &&
      !productsOpened.includes(matchProduct?.code)
    ) {
      this.openModalAndUpdateSession(
        productsOpened,
        sessionName,
        matchProductItem,
        items,
        text
      );
    }
  }

  getSessionProducts(sessionName: string): any[] {
    return this.sessionStorageSvc.getFromJson(sessionName) || [];
  }

  handleRemoveCase(
    productsOpened: any[],
    productsAddOpened: any[],
    items: Plans[],
    product: any,
    text: string
  ) {
    if (this.selectedProduct) {
      if (!product && !productsOpened.includes(this.selectedProduct?.code)) {
        this.openModalAndUpdateSession(
          productsOpened,
          'removeProduct',
          this.selectedProduct,
          items,
          text
        );
      } else if (product && !productsAddOpened.includes(product?.code)) {
        this.openModalAndUpdateSession(
          productsAddOpened,
          'addAndSaveProduct',
          product,
          items,
          this.addAndSavetext
        );
      }
    }
  }

  openModalAndUpdateSession(
    products: any[],
    sessionName: string,
    product: any,
    items: Plans[],
    text: string
  ) {
    products.push(product?.code);
    this.sessionStorageSvc.setAsJson(sessionName, products);
    this.bsModalRef = this.modalService.show(RollInRollOutModalComponent, {
      initialState: {
        product,
        selected: items,
        text
      }
    });
  }

  isCompositeProduct(product: string) {
    return (
      this.lastEmitObject &&
      this.lastEmitObject.product &&
      this.lastEmitObject.product.compositeProducts &&
      this.lastEmitObject.product.compositeProducts?.indexOf(
        product === OX_KEYS.MY_PLANS.RECURRING_UNLIMITED_30_KEY
          ? OX_KEYS.MY_PLANS.UNLIMITED_DATA_KEY
          : product
      ) >= 0
    );
  }

  isMyCurrentPlan(planCode: string) {
    const matchingOffer = this.updatePlanData?.offers?.find(
      (offer) => offer.planCode === planCode
    );
    return matchingOffer?.prices.some((price) => price.status === 'Active');
  }

  isIncludedInCurrentPlan(planCode: string) {
    return this.updatePlanData.offers.some(
      (offer) =>
        offer.prices.some((price) => price.status === 'Active') &&
        offer.compositeProducts?.includes(planCode)
    );
  }

  setValue(byoIndex: number, item: Plans, index: number, userAction: boolean) {
    const product = this.findProduct(item.offers[index].planCode);
    const offer = this.findOffer(product?.code);

    const updatedProduct = this.updateProduct(
      product,
      offer,
      item.offers,
      index,
      byoIndex
    );

    if (this.byoProducts[byoIndex].selected) {
      const text = this.addOrRemoveText(updatedProduct);
      updatedProduct.selected = this.byoProducts[byoIndex].selected;
      this.byoProducts[byoIndex] = updatedProduct;
      this.findAndEmitProduct(userAction ? text : '');
    } else {
      this.byoProducts[byoIndex] = updatedProduct;
    }
  }

  findProduct(planCode?: string) {
    return this.copy?.products?.find(
      (product: any) => product.code === planCode
    );
  }

  findOffer(planCode?: string) {
    return this.updatePlanData?.offers?.find(
      (item: any) => item.planCode === planCode
    );
  }

  updateProduct(
    product: any,
    offer: any,
    offers: any,
    index: number,
    byoIndex: number
  ) {
    const price = offer?.prices.find(
      (item: any) => item.billingFrequency === OX_KEYS.MY_PLANS.MONTHLY_KEY
    );

    return {
      ...product,
      offer,
      price,
      offers,
      selectedOfferIndex: index,
      multiCardDisplayName: this.byoProducts[byoIndex].multiCardDisplayName,
      annualDiscountTaglineCopy: offer?.annualDiscountTaglineCopy,
      selected: false
    };
  }

  toTitleCase(value: string) {
    return value.replace(/\w\S*/g, function (text: string) {
      return text.charAt(0).toUpperCase() + text.substring(1).toLowerCase();
    });
  }

  replaceBrand(text: string, brand?: string) {
    if (brand && brand !== 'GMC') {
      brand = this.toTitleCase(brand);
    }
    return replaceVariable(text, {
      Brand: brand || ''
    });
  }

  openServiceModal(product: any, featureValue: any) {
    this.sampleplanindex = 0;
    product.features.forEach((ele: any, index: number) => {
      if (ele.name === featureValue.name) {
        this.sampleplanindex = index;
      }
    });
    this.bsModalRef = this.modalService.show(ServiceModalComponent, {
      initialState: {
        featureByo: [product.features],
        featureValue: [featureValue],
        activeProduct: product
      }
    });
  }

  getCopy(copyKey: string) {
    return this.shopDataService.getGenericCopy(copyKey);
  }
}
